import { signUp } from 'aws-amplify/auth';
import BaseService from 'services/base-service';
import { trackPromise } from 'react-promise-tracker';
import type {
  ISignUpFormField,
  ITermsConditionData,
  saveTermsConditionRequestData,
  IReferralDetail,
  IRegionConfigurations,
  ISignUpResponse,
  IObjectRequest,
  IDriverVerified,
  IDriverVerifiedData,
  ICheckEmailExists,
  ICheckEmailExistsResponse,
  IPromotionDetail,
  ISaveMarketingPreferenceRequest,
  IEmailNotificationRequest,
  IPasswordStrengthData,
} from 'models/login';
import { StandardResponse, createStandardResponse } from 'models/standard-response';
import type { IRegionData } from 'models/profile';
import { ISupportedLanguage } from 'store/state/language.state';
import axios from 'axios';
import { store } from 'store';
import { env } from 'config/env';

export const cognitoSignup = async (data: ISignUpFormField): Promise<ISignUpResponse> => {
  try {
    const res = await trackPromise(
      signUp({
        username: data.email.toLowerCase(),
        password: data.password,
        options: {
          userAttributes: {
            'custom:language': data?.language_preference_name?.toLowerCase(),
          },
          validationData: {
            captcha_code: data.recaptchaCode,
            captcha_version: data.captcha_version,
            captcha_action: 'signup',
          },
        },
      }),
    );

    const objRequest = {
      email: data.email.toLowerCase(),
      provider_key: res.userId,
      platform_type: 'WEB',
      language_preference_id: data.language_preference_id,
      term_and_condition: data.term_and_condition,
    };
    const signUpResponse = signup(objRequest);
    return Promise.resolve(signUpResponse);
  } catch (err) {
    return Promise.reject(err);
  }
};

export const signup = async (data: IObjectRequest): Promise<ISignUpResponse> => {
  try {
    const res = await BaseService({
      url: `/driver/signup`,
      method: 'POST',
      data: { ...data, byPassAuth: true },
    });

    return Promise.resolve(res);
  } catch (err) {
    return Promise.reject(err);
  }
};

export const emailNotification = async (data: IEmailNotificationRequest): Promise<boolean> => {
  try {
    await BaseService({
      url: `/driver/notify-password-change`,
      method: 'PUT',
      data: { ...data, byPassAuth: true },
    });
    return Promise.resolve(true);
  } catch (_e) {
    return Promise.resolve(false);
  }
};

export const verifyReferaalCode = async (code: string): Promise<boolean> => {
  try {
    await BaseService({
      url: `/driver/check-referral-code/${code}`,
      method: 'GET',
    });
    return true;
  } catch (_e) {
    return false;
  }
};
export const checkReferralCode = async (referral_code: string): Promise<StandardResponse<IReferralDetail>> => {
  try {
    const res = await BaseService({
      url: `/driver/check-referral-code/${referral_code}`,
      method: 'GET',
    });
    return createStandardResponse<IReferralDetail>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};
export const checkPromotionCode = async (promotionCode: string): Promise<StandardResponse<IPromotionDetail>> => {
  try {
    const res = await BaseService({
      url: `/driver/check-promotion-code/${promotionCode}`,
      method: 'GET',
    });

    return createStandardResponse<IPromotionDetail>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};
export const saveTermsAndCondition = async (data: saveTermsConditionRequestData): Promise<StandardResponse<number>> => {
  try {
    const res = await BaseService({
      url: `/driver/referral-promotion-terms`,
      method: 'POST',
      data,
    });
    return createStandardResponse<number>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};
export const checkDriverIsVerified = async (data: IDriverVerifiedData): Promise<StandardResponse<IDriverVerified>> => {
  try {
    const res = await BaseService({
      url: `/driver/verify`,
      method: 'PUT',
      data: { ...data, byPassAuth: true },
    });
    return createStandardResponse<IDriverVerified>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};
export const checkEmailAlreadyExists = async (
  data: ICheckEmailExists,
): Promise<StandardResponse<ICheckEmailExistsResponse>> => {
  try {
    const res = await BaseService({
      url: `/driver/driver-log-config`,
      method: 'POST',
      data: { ...data, byPassAuth: true },
    });

    return createStandardResponse<ICheckEmailExistsResponse>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getSupportedLanguages = async (): Promise<{
  data: { rows: ISupportedLanguage[] };
}> => {
  try {
    const res = await axios.post(`${env.VITE_API_SERVER}/${env.VITE_API_VERSION}/driver/languages/search`, {});
    return Promise.resolve(res.data);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getRegions = async (language?: string): Promise<{ data: { rows: IRegionData[] } }> => {
  const languageCode = store?.getState()?.LanguageInfo?.defaultLanguage?.code
    ? store?.getState()?.LanguageInfo?.defaultLanguage?.code
    : 'en';
  const headers = {
    language: language || languageCode,
    Authorization: env.VITE_GLOBAL_SERVICE_API_TOKEN,
  };
  try {
    const res = await axios.post(
      `${env.VITE_GLOBAL_API_SERVER}/v1.0/global-services/regions/search`,
      {},
      {
        headers: headers,
      },
    );
    return Promise.resolve(res.data);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getRegionConfigurations = async (): Promise<{
  data: IRegionConfigurations;
}> => {
  try {
    const res = await axios.get(`${env.VITE_API_SERVER}/${env.VITE_API_VERSION}/driver/get-region-configs`);
    return Promise.resolve(res.data);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getTermAndCondition = async (): Promise<StandardResponse<ITermsConditionData>> => {
  try {
    const res = await BaseService({
      url: `driver/terms-and-conditions?appType=DRIVER_APP`,
      method: 'GET',
      data: { byPassAuth: true },
    });
    return createStandardResponse<ITermsConditionData>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const saveMarketingPreferenceForUser = async (
  data: ISaveMarketingPreferenceRequest,
): Promise<StandardResponse<boolean>> => {
  try {
    const res = await BaseService({
      url: `/driver/marketing-preference`,
      method: 'PUT',
      data,
    });
    return createStandardResponse<boolean>(res.status, res.data.data, res.data.message, null);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const validatePasswordStrength = async (password: string): Promise<StandardResponse<IPasswordStrengthData>> => {
  try {
    const headers = {
      language:
        store?.getState()?.UserData?.language?.code || store?.getState()?.LanguageInfo?.defaultLanguage?.code || 'en',
    };
    const response = await axios.post(
      `${env.VITE_API_SERVER}/common/driver/public/password/strength`,
      { password },
      { headers },
    );
    return createStandardResponse<IPasswordStrengthData>(
      response.status,
      response.data.data,
      response.data.message,
      null,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};
